/**
 * Caching Strategy Service
 * 
 * Advanced caching strategies for different data types with intelligent
 * cache invalidation, background refresh, and performance optimization.
 * 
 * Features:
 * - Data-type specific caching strategies
 * - Intelligent cache invalidation
 * - Background refresh for critical data
 * - Cache warming and preloading
 * - Performance monitoring and analytics
 * - Memory management and cleanup
 */

import { QueryClient } from '@tanstack/react-query';
import { queryClient } from '../lib/queryClient';
import { debugError, debugGroup, debugLog } from '../utils/debugLog';

// Cache strategy configuration for different data types
export interface CacheStrategy {
  staleTime: number;
  gcTime: number;
  refetchOnWindowFocus: boolean;
  refetchOnReconnect: boolean;
  refetchInterval?: number;
  refetchIntervalInBackground?: boolean;
  retry: boolean | number | ((failureCount: number, error: any) => boolean);
  retryDelay?: (attemptIndex: number) => number;
  backgroundRefresh?: boolean;
  preload?: boolean;
  priority: 'critical' | 'high' | 'medium' | 'low';
  description: string;
}

// Data type classifications
export enum DataType {
  PRODUCT_CATALOG = 'product-catalog',
  PRODUCT_DETAILS = 'product-details',
  COLLECTIONS = 'collections',
  CART_DATA = 'cart-data',
  USER_SESSION = 'user-session',
  SEARCH_RESULTS = 'search-results',
  HOMEPAGE_DATA = 'homepage-data',
  BLOG_CONTENT = 'blog-content',
  EVENTS_DATA = 'events-data',
  SCHEDULE_DATA = 'schedule-data',
  ROUTINE_SCHEDULE_DATA = 'routine-schedule-data',
  LIVE_DATA = 'live-data',
  PAST_SHOWS_DATA = 'past-shows-data',
  PAST_SHOWS_INFINITE_SCROLL = 'past-shows-infinite-scroll',
  PRESENTERS_DATA = 'presenters-data',
  INDIVIDUAL_SHOW_DATA = 'individual-show-data'
}

// Cache invalidation triggers
export enum InvalidationTrigger {
  USER_ACTION = 'user-action',
  TIME_BASED = 'time-based',
  DATA_MUTATION = 'data-mutation',
  EXTERNAL_EVENT = 'external-event',
  MANUAL = 'manual'
}

// Cache performance metrics
export interface CacheMetrics {
  hitRate: number;
  missRate: number;
  averageResponseTime: number;
  totalQueries: number;
  cacheSize: number;
  memoryUsage: number;
  invalidationCount: number;
  backgroundRefreshCount: number;
}

export interface CriticalPreloadOptions {
  include?: DataType[];
  exclude?: DataType[];
}

/**
 * Caching Strategy Service Class
 */
export class CachingStrategyService {
  private queryClient: QueryClient;
  private strategies: Map<DataType, CacheStrategy> = new Map();
  private metrics: Map<DataType, CacheMetrics> = new Map();
  private invalidationRules: Map<string, InvalidationTrigger[]> = new Map();
  private backgroundRefreshIntervals: Map<string, NodeJS.Timeout> = new Map();

  constructor(queryClient: QueryClient) {
    this.queryClient = queryClient;
    this.initializeStrategies();
    this.setupInvalidationRules();
  }

  /**
   * Initialize caching strategies for different data types
   */
  private initializeStrategies() {
    // Product catalog - stable data, long cache
    this.strategies.set(DataType.PRODUCT_CATALOG, {
      staleTime: 30 * 60 * 1000,        // 30 minutes
      gcTime: 2 * 60 * 60 * 1000,       // 2 hours
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: false,
      backgroundRefresh: true,
      preload: true,
      priority: 'high',
      description: 'Product catalog with background refresh for inventory updates'
    });

    // Product details - very stable, longer cache
    this.strategies.set(DataType.PRODUCT_DETAILS, {
      staleTime: 60 * 60 * 1000,        // 1 hour
      gcTime: 4 * 60 * 60 * 1000,       // 4 hours
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
      backgroundRefresh: false,
      preload: false,
      priority: 'medium',
      description: 'Individual product details - stable content'
    });

    // Collections - very stable, longest cache
    this.strategies.set(DataType.COLLECTIONS, {
      staleTime: 2 * 60 * 60 * 1000,    // 2 hours
      gcTime: 8 * 60 * 60 * 1000,       // 8 hours
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
      backgroundRefresh: false,
      preload: true,
      priority: 'low',
      description: 'Product collections - rarely change'
    });

    // Cart data - always fresh, critical priority
    this.strategies.set(DataType.CART_DATA, {
      staleTime: 0,                     // Always stale
      gcTime: 5 * 60 * 1000,           // 5 minutes
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: false,
      backgroundRefresh: false,
      preload: false,
      priority: 'critical',
      description: 'Shopping cart - must be always fresh'
    });

    // User session - medium freshness
    this.strategies.set(DataType.USER_SESSION, {
      staleTime: 10 * 60 * 1000,       // 10 minutes
      gcTime: 30 * 60 * 1000,          // 30 minutes
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: false,
      backgroundRefresh: false,
      preload: false,
      priority: 'high',
      description: 'User session data - moderate freshness required'
    });

    // Search results - short cache, user-driven
    this.strategies.set(DataType.SEARCH_RESULTS, {
      staleTime: 5 * 60 * 1000,        // 5 minutes
      gcTime: 15 * 60 * 1000,          // 15 minutes
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
      backgroundRefresh: false,
      preload: false,
      priority: 'medium',
      description: 'Search results - short-lived, user-specific'
    });

    // Homepage data - moderate cache with background refresh and retry
    this.strategies.set(DataType.HOMEPAGE_DATA, {
      staleTime: 15 * 60 * 1000,       // 15 minutes
      gcTime: 60 * 60 * 1000,          // 1 hour
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchInterval: 30 * 60 * 1000, // 30 minutes
      refetchIntervalInBackground: true,
      retry: 3,                        // Enable automatic retries (was false)
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      backgroundRefresh: true,
      preload: true,                   // Preload on startup for faster homepage render
      priority: 'high',
      description: 'Homepage content with periodic background updates and automatic retry'
    });

    // Blog content - long cache with retry capability for better reliability
    this.strategies.set(DataType.BLOG_CONTENT, {
      staleTime: 60 * 60 * 1000,       // 1 hour - blog content is relatively stable
      gcTime: 4 * 60 * 60 * 1000,      // 4 hours - keep in memory longer for better UX
      refetchOnWindowFocus: false,     // Don't refetch on focus - content is stable
      refetchOnReconnect: true,        // Refetch on reconnect in case of network issues
      retry: 2,                        // Retry failed requests (was false)
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff, max 10s
      backgroundRefresh: false,        // No background refresh - content doesn't change frequently
      preload: false,                  // Don't preload - user-initiated
      priority: 'medium',              // Upgraded from 'low' - blog content is important for user engagement
      description: 'Blog posts and articles - stable content with retry capability for reliability'
    });

    // Events data - moderate cache with background refresh
    this.strategies.set(DataType.EVENTS_DATA, {
      staleTime: 20 * 60 * 1000,       // 20 minutes
      gcTime: 2 * 60 * 60 * 1000,      // 2 hours
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchInterval: 60 * 60 * 1000, // 1 hour
      refetchIntervalInBackground: true,
      retry: false,
      backgroundRefresh: true,
      preload: true,
      priority: 'medium',
      description: 'Events and schedule data with periodic updates'
    });

    // Schedule data - short cache, frequent updates
    this.strategies.set(DataType.SCHEDULE_DATA, {
      staleTime: 10 * 60 * 1000,       // 10 minutes
      gcTime: 30 * 60 * 1000,          // 30 minutes
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchInterval: 15 * 60 * 1000, // 15 minutes
      refetchIntervalInBackground: true,
      retry: false,
      backgroundRefresh: true,
      preload: true,
      priority: 'high',
      description: 'Radio schedule - frequent updates required'
    });

    // Routine schedule data - 24-hour cache, infrequent updates
    this.strategies.set(DataType.ROUTINE_SCHEDULE_DATA, {
      staleTime: 24 * 60 * 60 * 1000,  // 24 hours
      gcTime: 48 * 60 * 60 * 1000,     // 48 hours
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchInterval: undefined,      // No automatic refetch
      refetchIntervalInBackground: false,
      retry: false,
      backgroundRefresh: false,
      preload: false,
      priority: 'low',
      description: 'Routine schedule - changes infrequently, 24-hour cache'
    });

    // Live data - very short cache, real-time updates
    this.strategies.set(DataType.LIVE_DATA, {
      staleTime: 30 * 1000,            // 30 seconds
      gcTime: 2 * 60 * 1000,           // 2 minutes
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      refetchInterval: 30 * 1000,      // 30 seconds - matches expected polling frequency
      refetchIntervalInBackground: true,
      retry: false,
      backgroundRefresh: true,
      preload: false,
      priority: 'critical',
      description: 'Live radio data - real-time updates every 30 seconds'
    });

    // Past shows data - immutable filenames allow longer client cache
    this.strategies.set(DataType.PAST_SHOWS_DATA, {
      staleTime: 6 * 60 * 60 * 1000,   // 6 hours - historical data rarely mutates
      gcTime: 12 * 60 * 60 * 1000,     // 12 hours - keep results warm across sessions
      refetchOnWindowFocus: false,     // Don't refetch on focus - historical data
      refetchOnReconnect: true,        // Refetch on reconnect for freshness
      retry: false,                    // Using ApiRetryService instead
      backgroundRefresh: false,        // No background refresh - updated via triggers
      preload: false,                  // Don't preload - user-initiated
      priority: 'high',                // Important for show browsing
      description: 'Past shows data - historical recordings cached for 6h (manual invalidation via admin actions)'
    });

    // Infinite scroll pagination - leverage immutable assets but keep cursors timely
    this.strategies.set(DataType.PAST_SHOWS_INFINITE_SCROLL, {
      staleTime: 3 * 60 * 60 * 1000,   // 3 hours - long enough for gallery reuse
      gcTime: 6 * 60 * 60 * 1000,      // 6 hours - retain cached pages during sessions
      refetchOnWindowFocus: false,     // Don't refetch on focus - avoid unnecessary requests
      refetchOnReconnect: true,        // Refetch on reconnect - ensure data consistency
      retry: false,                    // Using ApiRetryService instead
      backgroundRefresh: false,        // No background refresh - global detection handles this
      preload: false,                  // Don't preload - infinite scroll loads on demand
      priority: 'high',                // Important for pagination performance
      description: 'Infinite scroll pagination - 3h cache window, cursors refreshed on reconnect'
    });

    // Presenters data - long cache, rarely changes
    this.strategies.set(DataType.PRESENTERS_DATA, {
      staleTime: 12 * 60 * 60 * 1000,  // 12 hours - presenters/images are effectively immutable
      gcTime: 48 * 60 * 60 * 1000,     // 48 hours - keep in memory for extended browsing
      refetchOnWindowFocus: false,     // Don't refetch on focus - stable data
      refetchOnReconnect: false,       // Don't refetch on reconnect - stable data
      retry: false,                    // Using ApiRetryService instead
      backgroundRefresh: false,        // No background refresh - rarely changes
      preload: true,                   // Preload - presenters used on multiple surfaces
      priority: 'high',                // High priority - supporting multiple views
      description: 'Presenters data - immutable filenames allow 12h cache; invalidate via admin tasks when needed'
    });

    // Individual show data - infinite cache, immutable once recorded
    this.strategies.set(DataType.INDIVIDUAL_SHOW_DATA, {
      staleTime: Infinity,             // Never stale - individual shows never change
      gcTime: 24 * 60 * 60 * 1000,     // 24 hours - keep in memory for long sessions
      refetchOnWindowFocus: false,     // Don't refetch - immutable data
      refetchOnReconnect: false,       // Don't refetch - immutable data
      retry: 3,                        // Retry failed requests for reliability
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      backgroundRefresh: false,        // No background refresh - immutable data
      preload: false,                  // Don't preload - user-initiated
      priority: 'high',                // High priority - user is viewing specific show
      description: 'Individual show data - immutable content with infinite cache'
    });
  }

  /**
   * Setup cache invalidation rules
   */
  private setupInvalidationRules() {
    // Cart operations invalidate cart data
    this.invalidationRules.set('cart-mutation', [
      InvalidationTrigger.DATA_MUTATION,
      InvalidationTrigger.USER_ACTION
    ]);

    // Product updates invalidate catalog
    this.invalidationRules.set('product-update', [
      InvalidationTrigger.DATA_MUTATION,
      InvalidationTrigger.EXTERNAL_EVENT
    ]);

    // User actions invalidate session
    this.invalidationRules.set('user-action', [
      InvalidationTrigger.USER_ACTION
    ]);

    // Time-based invalidation for live data
    this.invalidationRules.set('live-data-refresh', [
      InvalidationTrigger.TIME_BASED
    ]);
  }

  /**
   * Get caching strategy for a data type
   */
  getStrategy(dataType: DataType | keyof typeof DataType): CacheStrategy {
    let normalizedType: DataType;

    if (typeof dataType === 'string' && (DataType as Record<string, string>)[dataType]) {
      normalizedType = (DataType as Record<string, string>)[dataType] as DataType;
    } else {
      normalizedType = dataType as DataType;
    }

    if (!this.strategies.has(normalizedType)) {
      // Re-seed strategies in case a test or consumer cleared the map
      this.initializeStrategies();
    }

    const strategy = this.strategies.get(normalizedType);
    if (!strategy) {
      throw new Error(`No caching strategy defined for data type: ${normalizedType}`);
    }
    return strategy;
  }

  /**
   * Apply caching strategy to a query key pattern
   */
  applyCachingStrategy(queryKeyPattern: string, dataType: DataType): CacheStrategy {
    const strategy = this.getStrategy(dataType);

    debugGroup(`Applied ${dataType} caching strategy to ${queryKeyPattern}`, () => {
      debugLog({
        staleTime: `${strategy.staleTime / 1000}s`,
        gcTime: `${strategy.gcTime / 1000}s`,
        priority: strategy.priority,
        backgroundRefresh: strategy.backgroundRefresh,
      });
    });

    return strategy;
  }

  /**
   * Invalidate cache based on trigger
   */
  async invalidateCache(
    queryKeyPattern: string | string[],
    trigger: InvalidationTrigger,
    options: {
      exact?: boolean;
      refetch?: boolean;
      removeFromCache?: boolean;
    } = {}
  ) {
    const { exact = false, refetch = true, removeFromCache = false } = options;

    try {
      if (exact) {
        // Exact match invalidation
        await this.queryClient.invalidateQueries({
          queryKey: Array.isArray(queryKeyPattern) ? queryKeyPattern : [queryKeyPattern],
          exact: true,
          refetchType: refetch ? 'active' : 'none'
        });
      } else {
        // Pattern-based invalidation
        await this.queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKeyStr = JSON.stringify(query.queryKey);
            if (Array.isArray(queryKeyPattern)) {
              return queryKeyPattern.some(pattern => queryKeyStr.includes(pattern));
            }
            return queryKeyStr.includes(queryKeyPattern);
          },
          refetchType: refetch ? 'active' : 'none'
        });
      }

      if (removeFromCache) {
        this.queryClient.removeQueries({
          predicate: (query) => {
            const queryKeyStr = JSON.stringify(query.queryKey);
            if (Array.isArray(queryKeyPattern)) {
              return queryKeyPattern.some(pattern => queryKeyStr.includes(pattern));
            }
            return queryKeyStr.includes(queryKeyPattern);
          }
        });
      }

      // Update metrics
      this.updateInvalidationMetrics(queryKeyPattern, trigger);

      debugLog(`Cache invalidated for pattern: ${queryKeyPattern}, trigger: ${trigger}`);
    } catch (error) {
      debugError('Cache invalidation failed:', error);
      throw error;
    }
  }

  /**
   * Manual method to force refresh live show data (for debugging)
   */
  async forceRefreshLiveShow() {
    debugLog('🔄 Forcing live show cache refresh...');
    await this.invalidateCache(['liveShow'], InvalidationTrigger.MANUAL);
    
    // Also trigger a fresh fetch
    try {
      const { ApiService } = await import('./apiService');
      const response = await ApiService.getLiveShow();
      debugLog('🔄 Fresh live show data:', response);
      return response;
    } catch (error) {
      debugError('❌ Error fetching fresh live show data:', error);
      return null;
    }
  }

  /**
   * Manual method to force refresh blog content (for content management scenarios)
   */
  async forceRefreshBlogContent() {
    debugLog('🔄 Forcing blog content cache refresh...');
    await this.invalidateCache(['blog'], InvalidationTrigger.MANUAL, {
      refetch: true,
      removeFromCache: false
    });
    
    // Also trigger a fresh fetch for the main blog listing
    try {
      const { cmsApiService } = await import('./cmsApiService');
      const response = await cmsApiService.getBlogPosts({ limit: 50 });
      debugLog('🔄 Fresh blog data:', response);
      return response;
    } catch (error) {
      debugError('❌ Error fetching fresh blog data:', error);
      return null;
    }
  }

  /**
   * Preload critical data
   */
  async preloadCriticalData(options: CriticalPreloadOptions = {}) {
    const includeSet = options.include ? new Set(options.include) : null;
    const excludeSet = options.exclude ? new Set(options.exclude) : null;

    const criticalStrategies = Array.from(this.strategies.entries())
      .filter(([dataType, strategy]) => {
        if (!strategy.preload || (strategy.priority !== 'critical' && strategy.priority !== 'high')) {
          return false;
        }

        if (includeSet && !includeSet.has(dataType)) {
          return false;
        }

        if (excludeSet?.has(dataType)) {
          return false;
        }

        return true;
      });

    const preloadPromises = criticalStrategies.map(async ([dataType, strategy]) => {
      try {
        // Define preload queries based on data type
        switch (dataType) {
          case DataType.PRODUCT_CATALOG:
            await this.queryClient.prefetchQuery({
              queryKey: ['shopify-products', 20, null, 'TITLE', false, null, 'optimized'],
              queryFn: () => this.fetchProductCatalog(),
              ...strategy
            });
            break;
          
          case DataType.COLLECTIONS:
            await this.queryClient.prefetchQuery({
              queryKey: ['shopify-collections', 20, null, 'TITLE', false, 'optimized'],
              queryFn: () => this.fetchCollections(),
              ...strategy
            });
            break;
          
          case DataType.HOMEPAGE_DATA:
            await this.queryClient.prefetchQuery({
              queryKey: ['homepageData', {blog_count: 3, event_count: 3, gallery_count: 1, featured_only: false}],
              queryFn: async () => {
                const { homepageApiService } = await import('./homepageApiService');
                const response = await homepageApiService.getHomepageData({
                  blog_count: 3,
                  event_count: 3,
                  gallery_count: 1,
                  featured_only: false
                });
                if (!response.success) {
                  throw new Error(response.error || 'Failed to fetch homepage data');
                }
                return response.data;
              },
              staleTime: strategy.staleTime,
              gcTime: strategy.gcTime,
            });
            break;
          
          case DataType.PRESENTERS_DATA:
            await this.queryClient.prefetchQuery({
              queryKey: ['presenters'],
              queryFn: async () => {
                const { ApiService } = await import('./apiService');
                const response = await ApiService.getPresenters();
                if (!response.success) {
                  throw new Error(response.error || 'Failed to fetch presenters');
                }
                return response.data;
              },
              staleTime: strategy.staleTime,
              gcTime: strategy.gcTime,
              retry: strategy.retry,
            });
            break;
            
          case DataType.SCHEDULE_DATA:
            await this.queryClient.prefetchQuery({
              queryKey: ['schedule'],
              queryFn: async () => {
                const { ScheduleApiService } = await import('./scheduleApiService');
                const response = await ScheduleApiService.getSchedule();
                if (!response.success) {
                  throw new Error(response.error || 'Failed to fetch schedule');
                }
                return response.data;
              },
              staleTime: strategy.staleTime,
              gcTime: strategy.gcTime,
            });
            break;
            
          case DataType.ROUTINE_SCHEDULE_DATA:
            await this.queryClient.prefetchQuery({
              queryKey: ['routineSchedule'],
              queryFn: async () => {
                const { ApiService } = await import('./apiService');
                const response = await ApiService.getRoutineSchedule();
                if (!response.success) {
                  throw new Error(response.error || 'Failed to fetch routine schedule');
                }
                return response.data;
              },
              staleTime: strategy.staleTime,
              gcTime: strategy.gcTime,
            });
            break;
            
          case DataType.EVENTS_DATA:
            await this.queryClient.prefetchQuery({
              queryKey: ['cms', '/event'],
              queryFn: async () => {
                const { cmsApiService } = await import('./cmsApiService');
                return await cmsApiService.getEvents();
              },
              staleTime: strategy.staleTime,
              gcTime: strategy.gcTime,
            });
            break;
        }
    } catch (error) {
      debugLog(`Failed to preload ${dataType}:`, error);
    }
    });

    await Promise.allSettled(preloadPromises);
    debugLog('Critical data preloading completed');
  }

  /**
   * Setup background refresh for applicable data types
   */
  setupBackgroundRefresh() {
    const backgroundRefreshStrategies = Array.from(this.strategies.entries())
      .filter(([_, strategy]) => strategy.backgroundRefresh);

    backgroundRefreshStrategies.forEach(([dataType, strategy]) => {
      const interval = strategy.refetchInterval || 30 * 60 * 1000; // Default 30 minutes
      
      const intervalId = setInterval(async () => {
        try {
          await this.refreshDataType(dataType);
    } catch (error) {
      debugLog(`Background refresh failed for ${dataType}:`, error);
    }
      }, interval);

      this.backgroundRefreshIntervals.set(dataType, intervalId);
    });

    debugLog(`Background refresh setup for ${backgroundRefreshStrategies.length} data types`);
  }

  /**
   * Refresh specific data type
   */
  private async refreshDataType(dataType: DataType) {
    switch (dataType) {
      case DataType.PRODUCT_CATALOG:
        await this.invalidateCache('shopify-products', InvalidationTrigger.TIME_BASED, {
          refetch: true
        });
        break;
      
      case DataType.HOMEPAGE_DATA:
        await this.invalidateCache('homepage-data', InvalidationTrigger.TIME_BASED, {
          refetch: true
        });
        break;
      
      case DataType.BLOG_CONTENT:
        await this.invalidateCache('blog', InvalidationTrigger.TIME_BASED, {
          refetch: true
        });
        break;
      
      case DataType.EVENTS_DATA:
        await this.invalidateCache('events', InvalidationTrigger.TIME_BASED, {
          refetch: true
        });
        break;
      
      case DataType.SCHEDULE_DATA:
        await this.invalidateCache('schedule', InvalidationTrigger.TIME_BASED, {
          refetch: true
        });
        break;
      
      case DataType.ROUTINE_SCHEDULE_DATA:
        await this.invalidateCache('routineSchedule', InvalidationTrigger.TIME_BASED, {
          refetch: true
        });
        break;
      
      case DataType.LIVE_DATA:
        await this.invalidateCache('live-show', InvalidationTrigger.TIME_BASED, {
          refetch: true
        });
        break;
      
      case DataType.PAST_SHOWS_DATA:
        await this.invalidateCache('shows', InvalidationTrigger.TIME_BASED, {
          refetch: true
        });
        break;
      
      case DataType.PRESENTERS_DATA:
        await this.invalidateCache('presenters', InvalidationTrigger.TIME_BASED, {
          refetch: true
        });
        break;
      
      case DataType.INDIVIDUAL_SHOW_DATA:
        // Individual shows are immutable, no need for time-based refresh
        // Only invalidate if explicitly requested
        break;
    }
  }

  /**
   * Get cache performance metrics
   */
  getCacheMetrics(): Map<DataType, CacheMetrics> {
    const cache = this.queryClient.getQueryCache();
    const queries = cache.getAll();

    // Calculate metrics for each data type
    this.strategies.forEach((strategy, dataType) => {
      const dataTypeQueries = queries.filter(query => 
        this.isQueryOfDataType(query.queryKey, dataType)
      );

      const totalQueries = dataTypeQueries.length;
      const staleQueries = dataTypeQueries.filter(q => q.isStale()).length;
      const metrics: CacheMetrics = {
        hitRate: totalQueries > 0 ? (totalQueries - staleQueries) / totalQueries : 0,
        missRate: totalQueries > 0 ? staleQueries / totalQueries : 0,
        averageResponseTime: this.calculateAverageResponseTime(dataTypeQueries),
        totalQueries,
        cacheSize: dataTypeQueries.length,
        memoryUsage: this.estimateMemoryUsage(dataTypeQueries),
        invalidationCount: this.getInvalidationCount(dataType),
        backgroundRefreshCount: this.getBackgroundRefreshCount(dataType)
      };

      this.metrics.set(dataType, metrics);
    });

    return this.metrics;
  }

  /**
   * Cleanup expired cache entries
   */
  cleanupExpiredEntries(): number {
    const cache = this.queryClient.getQueryCache();
    const queries = cache.getAll();
    let cleanedCount = 0;

    queries.forEach(query => {
      const strategy = this.getStrategyForQuery(query.queryKey);
      if (strategy && this.isQueryExpired(query, strategy)) {
        this.queryClient.removeQueries({ queryKey: query.queryKey });
        cleanedCount++;
      }
    });

    debugLog(`Cleaned up ${cleanedCount} expired cache entries`);
    return cleanedCount;
  }

  /**
   * Optimize cache memory usage
   */
  optimizeMemoryUsage() {
    const cache = this.queryClient.getQueryCache();
    const queries = cache.getAll();

    // Remove low-priority stale queries
    const lowPriorityStaleQueries = queries.filter(query => {
      const strategy = this.getStrategyForQuery(query.queryKey);
      return strategy?.priority === 'low' && query.isStale();
    });

    lowPriorityStaleQueries.forEach(query => {
      this.queryClient.removeQueries({ queryKey: query.queryKey });
    });

    debugLog(`Optimized memory usage by removing ${lowPriorityStaleQueries.length} low-priority stale queries`);
  }

  /**
   * Destroy background refresh intervals
   */
  destroy() {
    this.backgroundRefreshIntervals.forEach((intervalId) => {
      clearInterval(intervalId);
    });
    this.backgroundRefreshIntervals.clear();
    debugLog('Caching strategy service destroyed');
  }

  // Helper methods
  private async fetchProductCatalog() {
    // Implementation would call actual API
    return { products: { edges: [] } };
  }

  private async fetchCollections() {
    // Implementation would call actual API
    return { collections: { edges: [] } };
  }

  private async fetchHomepageData() {
    // Implementation would call actual API
    return { homepage: {} };
  }

  private isQueryOfDataType(queryKey: readonly unknown[], dataType: DataType): boolean {
    const keyStr = JSON.stringify(queryKey);
    
    switch (dataType) {
      case DataType.PRODUCT_CATALOG:
        return keyStr.includes('shopify-products');
      case DataType.PRODUCT_DETAILS:
        return keyStr.includes('product-detail');
      case DataType.COLLECTIONS:
        return keyStr.includes('shopify-collections');
      case DataType.CART_DATA:
        return keyStr.includes('cart');
      case DataType.HOMEPAGE_DATA:
        return keyStr.includes('homepageData');
      case DataType.BLOG_CONTENT:
        return keyStr.includes('blog');
      case DataType.LIVE_DATA:
        return keyStr.includes('liveShow');
      case DataType.PAST_SHOWS_DATA:
        return keyStr.includes('shows') && !keyStr.includes('show","'); // Bulk shows, not individual
      case DataType.PRESENTERS_DATA:
        return keyStr.includes('presenters');
      case DataType.INDIVIDUAL_SHOW_DATA:
        return keyStr.includes('show","') && !keyStr.includes('shows'); // Individual show, not bulk
      case DataType.SCHEDULE_DATA:
        return keyStr.includes('schedule') && !keyStr.includes('routineSchedule');
      case DataType.ROUTINE_SCHEDULE_DATA:
        return keyStr.includes('routineSchedule');
      default:
        return false;
    }
  }

  private calculateAverageResponseTime(queries: any[]): number {
    // Simplified calculation - would need actual response time tracking
    return queries.length > 0 ? 150 : 0;
  }

  private estimateMemoryUsage(queries: any[]): number {
    // Simplified estimation - would need actual memory measurement
    return queries.reduce((total, query) => {
      return total + JSON.stringify(query.state.data || {}).length;
    }, 0);
  }

  private getInvalidationCount(dataType: DataType): number {
    // Would track actual invalidation counts
    return 0;
  }

  private getBackgroundRefreshCount(dataType: DataType): number {
    // Would track actual background refresh counts
    return 0;
  }

  private getStrategyForQuery(queryKey: readonly unknown[]): CacheStrategy | undefined {
    const entries = Array.from(this.strategies.entries());
    for (const [dataType, strategy] of entries) {
      if (this.isQueryOfDataType(queryKey, dataType)) {
        return strategy;
      }
    }
    return undefined;
  }

  private isQueryExpired(query: any, strategy: CacheStrategy): boolean {
    const now = Date.now();
    const queryTime = query.state.dataUpdatedAt || 0;
    return (now - queryTime) > strategy.gcTime;
  }

  private updateInvalidationMetrics(pattern: string | string[], trigger: InvalidationTrigger) {
    // Would update actual invalidation metrics
  }
}

// Create singleton instance with the global query client
const cachingStrategyService = new CachingStrategyService(queryClient);

// Default export
export default CachingStrategyService;

// Named export for singleton instance
export { cachingStrategyService };
